'use client';

import React, { useMemo } from 'react';
import { Box } from '@skin-seoul/design-system';
import { Carousel, useCarousel } from '@/components/ui/Carousel';
import { useDynamicCategories, type TaxonomyCategory, generateCategoryUrl } from '../utils/categoryUtils';
import CategoryCard from './CategoryCard';
import CategorySliderSkeleton from './CategorySliderSkeleton';
import CategorySliderNavigationButton from './CategorySliderNavigationButton';
interface CategorySliderProps {
  activeCategory?: string;
  subcategory?: string;
}
interface SliderContentProps {
  displayCategories: TaxonomyCategory[];
  activeSlug: string | undefined;
  getCategoryUrl: (category: TaxonomyCategory) => string;
}
const SliderContent = ({
  displayCategories,
  activeSlug,
  getCategoryUrl
}: SliderContentProps) => {
  const {
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext
  } = useCarousel();
  return <Box width="100%" position="relative" display="flex" alignItems="center" gap={0} data-sentry-element="Box" data-sentry-component="SliderContent" data-sentry-source-file="CategorySlider.tsx">
      {canScrollPrev && <Box flexShrink={0} display={{
      base: 'none',
      tablet: 'block'
    }} paddingLeft={3}>
          <CategorySliderNavigationButton direction="left" onClick={scrollPrev} ariaLabel="Previous categories" />
        </Box>}
      <Box flex="1" overflow="hidden" minWidth={0} data-sentry-element="Box" data-sentry-source-file="CategorySlider.tsx">
        <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="CategorySlider.tsx">
          {displayCategories.map(category => <Carousel.Item key={category.termId} flexBasis="auto" px={3} py={2}>
              <CategoryCard category={category} isActive={activeSlug === category.slug} href={getCategoryUrl(category)} />
            </Carousel.Item>)}
        </Carousel.Content>
      </Box>
      {canScrollNext && <Box flexShrink={0} display={{
      base: 'none',
      tablet: 'block'
    }} paddingRight={3}>
          <CategorySliderNavigationButton direction="right" onClick={scrollNext} ariaLabel="Next categories" />
        </Box>}
    </Box>;
};
const CategorySlider = ({
  activeCategory,
  subcategory
}: CategorySliderProps) => {
  const {
    categories
  } = useDynamicCategories();
  const displayCategories = useMemo(() => {
    if (!activeCategory || !categories.length) {
      return categories;
    }
    const currentParentCategory = categories.find(cat => cat.slug === activeCategory);
    if (currentParentCategory?.subCategories?.length) {
      return currentParentCategory.subCategories;
    }
    return categories;
  }, [categories, activeCategory]);
  const getCategoryUrl = (category: TaxonomyCategory) => {
    if (!activeCategory || !categories.length) {
      return `/collections/${category.slug}/`;
    } else {
      return generateCategoryUrl(categories, category.termId);
    }
  };
  const getActiveSlug = () => {
    if (!activeCategory || !categories.length) {
      return activeCategory;
    } else {
      return subcategory;
    }
  };
  const activeSlug = getActiveSlug();
  if (!displayCategories.length) {
    return <CategorySliderSkeleton />;
  }
  return <Carousel opts={{
    loop: false,
    align: 'start',
    dragFree: false
  }} width="100%" data-sentry-element="Carousel" data-sentry-component="CategorySlider" data-sentry-source-file="CategorySlider.tsx">
      <SliderContent displayCategories={displayCategories} activeSlug={activeSlug} getCategoryUrl={getCategoryUrl} data-sentry-element="SliderContent" data-sentry-source-file="CategorySlider.tsx" />
    </Carousel>;
};
export default CategorySlider;